import { Outlet } from 'react-router-dom';

/**
 * Split-screen shell for unauthenticated screens (login, forgot password).
 *
 * Left: brand hero panel with animated aurora background + product tagline.
 *      Hidden on <lg screens where the auth form takes the full width.
 * Right: renders the child route (LoginPage etc.) centered in its own column.
 */
export function PublicShell() {
  return (
    <div
      data-public-shell
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        background: 'var(--db-bg)',
      }}
    >
      <BrandHero />
      <div
        data-public-form-column
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 32px',
          background: '#ffffff',
        }}
      >
        <Outlet />
      </div>

      <style>{`
        @media (max-width: 960px) {
          div[data-brand-hero] { display: none !important; }
          div[data-public-shell] { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          div[data-public-form-column] { padding: 24px 16px !important; }
        }
      `}</style>
    </div>
  );
}

function BrandHero() {
  return (
    <div
      data-brand-hero
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #041228 0%, #02408B 55%, #1E5FB8 100%)',
        color: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px 56px',
        minHeight: '100vh',
      }}
    >
      {/* Aurora blobs */}
      <div
        className="db-aurora-blob"
        style={{
          top: '-8%',
          left: '-10%',
          width: 420,
          height: 420,
          background: 'radial-gradient(circle, #7B61FF 0%, transparent 70%)',
        }}
      />
      <div
        className="db-aurora-blob"
        style={{
          bottom: '-12%',
          right: '-8%',
          width: 460,
          height: 460,
          background: 'radial-gradient(circle, #4FA8FF 0%, transparent 70%)',
          animationDelay: '-6s',
        }}
      />
      <div
        className="db-aurora-blob"
        style={{
          top: '30%',
          right: '20%',
          width: 260,
          height: 260,
          background: 'radial-gradient(circle, #14B8A6 0%, transparent 70%)',
          animationDelay: '-3s',
        }}
      />

      {/* Top brand row — logo on a translucent white pill */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            height: 44,
            padding: '4px 10px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          }}
        >
          <img
            src="/logo.png"
            alt="D Table"
            style={{ maxHeight: 30, maxWidth: 130, objectFit: 'contain', display: 'block' }}
          />
        </div>
      </div>

      {/* Middle content */}
      <div style={{ position: 'relative', maxWidth: 440 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.18)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.4,
            marginBottom: 24,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ADE80' }} />
          Modern HR, reimagined
        </div>

        <h1
          style={{
            fontSize: 44,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: -1,
            margin: 0,
            marginBottom: 18,
          }}
        >
          Everything your people need,
          <br />
          <span
            style={{
              background: 'linear-gradient(90deg, #ffffff 0%, #4FA8FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            in one workspace.
          </span>
        </h1>
        <p style={{ fontSize: 15, opacity: 0.82, lineHeight: 1.6, margin: 0, maxWidth: 400 }}>
          Payroll, attendance, hiring, performance, and engagement — unified in a fast,
          role-aware platform your team will actually enjoy using.
        </p>

        {/* Feature pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 28 }}>
          {['Payroll', 'Attendance', 'Hiring', 'Performance', 'Documents', 'Assets'].map((f) => (
            <span
              key={f}
              style={{
                fontSize: 12,
                padding: '5px 12px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.16)',
                backdropFilter: 'blur(6px)',
              }}
            >
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom testimonial */}
      <div
        style={{
          position: 'relative',
          padding: 20,
          borderRadius: 14,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.14)',
          backdropFilter: 'blur(12px)',
          maxWidth: 440,
        }}
      >
        <div style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.94, fontStyle: 'italic' }}>
          "We replaced three separate tools with D Table. Onboarding is now under 10
          minutes and our managers finally look at the dashboards."
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(135deg,#4FA8FF,#7B61FF)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            P
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Priya Menon</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>Head of People · Truent Labs</div>
          </div>
        </div>
      </div>
    </div>
  );
}
